import React, { createContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Alert, CircularProgress } from "@mui/material";
import * as Sentry from "@sentry/react";
import ReactGA from "react-ga4";
import "./CheckoutPage.css";
import CartSubtitle from "./Components/CartSubtitle/CartSubtitle";
import CheckoutFooter from "./Components/CheckoutFooter/CheckoutFooter";
import UniversalCheckoutHeader from "../../UniversalComponents/CheckoutHeader/CheckoutHeader";
import CheckoutPaymentFooter from "./Components/CheckoutPaymentFooter/CheckoutPaymentFooter";
import PaymentFailedModal from "./Components/PaymentFailed/PaymentFailedModal";

import {
  FinancialData,
  ItemDetailsData,
  RecommendedItem,
  SchoolDetails,
  DataLinks,
} from "../../Utils/DefinitionsInterfaces";
import { merchantLogoString } from "../../Utils/Constants";
import {
  doNFOTransactionResponse,
  fetchRecommendedItems,
  getSchoolByCode,
  verifyNFOTransaction,
  verifyNFOTransactionRazorpay,
} from "../../Utils/CheckoutAPIs/CheckoutAPIs";
import PFHModal from "./Components/PFHModal/PFHModal";

// Add WebEngage type declaration
declare global {
  interface Window {
    webengage: {
      user: {
        login: (userId: string) => void;
        setAttribute: (
          attributeName: string,
          attributeValue: string | number | boolean | Date
        ) => void;
      };
      track: (eventName: string, eventData: Record<string, any>) => void;
    };
  }
}

// Add type declarations for window
declare global {
  interface Window {
    loadBillDeskSdk: (config: any) => void;
  }
}

// Define proper types for context
interface UserContextType {
  userDetails: {
    name: string;
    email: string;
    school: string;
    schoolID: number;
    grade: number;
    mobileNumber: string;
    address: string;
    flatNo: string;
    city: string;
    state: string;
    pincode: string;
    pfh?: boolean;
  };
  setUserDetails: React.Dispatch<
    React.SetStateAction<UserContextType["userDetails"]>
  >;
  backendSchool: SchoolDetails;
  setBackendSchool: React.Dispatch<React.SetStateAction<SchoolDetails>>;
  schoolID: number;
  setSchoolID: React.Dispatch<React.SetStateAction<number>>;
  individualReg: boolean;
  setIndividualReg: React.Dispatch<React.SetStateAction<boolean>>;
  examDate: string;
  setExamDate: React.Dispatch<React.SetStateAction<string>>;
  isOtpVerified: boolean;
  setIsOtpVerified: React.Dispatch<React.SetStateAction<boolean>>;
  pfhVerified: boolean;
  setPfhVerified: React.Dispatch<React.SetStateAction<boolean>>;
}

export const UserContext = createContext<UserContextType | null>(null);

interface CheckoutPageProps {
  history?: {
    push: (path: string) => void;
    replace: (path: string) => void;
  };
}

const CheckoutPage: React.FC<CheckoutPageProps> = ({ history }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);

  // State management
  const [paymentFailed, setPaymentFailed] = useState(false);
  const [verifyPaymentProgress, setVerifyPaymentProgress] = useState(false);
  const [isPaymentPageActive, setIsPaymentPageActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkoutItems, setCheckoutItems] = useState<ItemDetailsData[]>([]);
  const [recommendedItems, setRecommendedItems] = useState<RecommendedItem[]>(
    []
  );
  const [allItemDetailsDB, setAllItemDetailsDB] = useState<ItemDetailsData[]>(
    []
  );
  const [defaultItems, setDefaultItems] = useState<FinancialData[]>([]);
  const [checkoutItemIds, setCheckoutItemIds] = useState<number[]>([]);
  const [backendSchool, setBackendSchool] = useState<SchoolDetails>({
    schoolID: 0,
    schoolName: "",
    linkActive: false,
    city: "",
    state: "",
    individual_registration: false,
    examDate: "",
    isInternational: false,
    version_no: "V1",
    pg: "razorpay",
  });
  const [schoolID, setSchoolID] = useState(0);
  const [individualReg, setIndividualReg] = useState(false);
  const [examDate, setExamDate] = useState("");
  const sessionStorageMobileNumber = sessionStorage.getItem("mobileNumber");
  const [userDetails, setUserDetails] = useState({
    name: "",
    email: "",
    school: "",
    schoolID: 0,
    grade: 0,
    mobileNumber: "",
    address: "",
    flatNo: "",
    city: "",
    state: "",
    pincode: "",
  });
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [pfhVerified, setPfhVerified] = useState(false);
  const referredBy = urlParams.get("referredBy");

  // URL parameters
  const orderId = urlParams.get("order_id");
  const itemIds = urlParams.get("item_ids");
  const schoolCode = urlParams.get("scl");
  const nfoCheckoutAccessToken = urlParams.get("token");
  const [showAlert, setShowAlert] = useState({
    isAlertVisible: false,
    alert: "",
  });

  // Store token if present
  useEffect(() => {
    if (nfoCheckoutAccessToken) {
      localStorage.setItem("nfoCheckoutAccessToken", nfoCheckoutAccessToken);
    }
  }, [nfoCheckoutAccessToken]);

  // Handle school code
  useEffect(() => {
    if (schoolCode && schoolCode.length > 1) {
      sessionStorage.setItem("schoolCode", schoolCode);
    } else {
      const searchParams = new URLSearchParams({ scl: "IRS" }).toString();
      sessionStorage.setItem("schoolCode", "IRS");
      const newUrl = `/CheckoutPage/?${searchParams}`;
      navigate(newUrl);
    }
  }, [schoolCode, navigate]);

  // Payment verification functions
  const verifyPaymentInternational = async (
    orderId: string
  ): Promise<boolean> => {
    try {
      const response = await fetch(
        "https://streak-payments.el.r.appspot.com/nfo/stripe-metadata",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ sessionId: orderId }),
        }
      );

      const data = await response.json();
      return (
        data.data.message ===
        "Metadata retrieved, student onboarded, and invoice sent successfully."
      );
    } catch (err) {
      Sentry.captureException(err);
      return false;
    }
  };

  console.log("is otp verified", isOtpVerified);

  const verifyPayment = async (orderId: string): Promise<boolean> => {
    try {
      const errorStatuses = [
        "payment_processing_error",
        "user_cancelled_error",
        "acquirer_processing_error",
        "risk_validation_error",
      ];

      const retryPayment = async (
        remainingRetries: number
      ): Promise<boolean> => {
        while (remainingRetries > 0) {
          const newResponse = await verifyNFOTransaction(orderId);
          const newPaymentStatus = newResponse.data.links[0].method;

          if (newPaymentStatus === "success") return true;
          if (newPaymentStatus === "retry") {
            remainingRetries--;
            await new Promise((resolve) => setTimeout(resolve, 1000));
          } else if (errorStatuses.includes(newPaymentStatus)) return false;
          else return false;
        }
        return false;
      };

      const response = await verifyNFOTransaction(orderId);
      const paymentStatus = response.data.links[0].method;

      if (paymentStatus === "success") return true;
      if (paymentStatus === "retry") return await retryPayment(3);
      if (errorStatuses.includes(paymentStatus)) return false;
      return false;
    } catch (err) {
      Sentry.captureException(err);
      return false;
    }
  };

  // Add missing verifyPaymentRazorpay function
  const verifyPaymentRazorpay = async (orderId: string): Promise<boolean> => {
    try {
      const retryPayment = async (
        remainingRetries: number
      ): Promise<boolean> => {
        while (remainingRetries > 0) {
          const newResponse = await verifyNFOTransactionRazorpay(orderId);
          const newPaymentStatus = newResponse.data.status;

          if (newPaymentStatus === "paid") return true;
          if (newPaymentStatus === "created") {
            remainingRetries--;
            await new Promise((resolve) => setTimeout(resolve, 1000));
          } else if (newPaymentStatus === "attempted") return false;
          else return false;
        }
        return false;
      };

      const response = await verifyNFOTransactionRazorpay(orderId);
      const paymentStatus = response.data.status;

      if (paymentStatus === "paid") return true;
      if (paymentStatus === "created") return await retryPayment(3);
      if (paymentStatus === "attempted") return false;
      return false;
    } catch (err) {
      Sentry.captureException(err);
      return false;
    }
  };

  // Add paymentCallbackFunctionInternational
  const paymentCallbackFunctionInternational = async () => {
    if (!orderId) return;

    setVerifyPaymentProgress(true);
    try {
      const orderStatus = await verifyPaymentInternational(orderId);

      if (orderStatus) {
        setVerifyPaymentProgress(false);
        navigate(`/PaymentSuccessful/?&scl=${schoolCode}&order_id=${orderId}`);
        return;
      }

      // Retry verification
      await new Promise((resolve) => setTimeout(resolve, 2000));
      const orderStatus2ndTime = await verifyPayment(orderId);

      if (orderStatus2ndTime) {
        setVerifyPaymentProgress(false);
        navigate(
          `/PaymentSuccessful/?&scl=${schoolCode}&order_id=${orderId}${
            itemIds ? `&item_ids=${itemIds}` : ""
          }`
        );
        return;
      }

      // Final retry
      await new Promise((resolve) => setTimeout(resolve, 2000));
      const orderStatus3rdTime = await verifyPayment(orderId);

      if (orderStatus3rdTime) {
        setVerifyPaymentProgress(false);
        navigate(
          `/PaymentSuccessful/?&scl=${schoolCode}&order_id=${orderId}${
            itemIds ? `&item_ids=${itemIds}` : ""
          }`
        );
        return;
      }

      // Final verification with external API
      const response = await fetch(
        "https://streak-payments.el.r.appspot.com/nfo/stripe-metadata",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ sessionId: orderId }),
        }
      );

      const data = await response.json();
      if (
        data.data.message !==
        "Metadata retrieved and student onboarded successfully"
      ) {
        setPaymentFailed(true);
      }
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setVerifyPaymentProgress(false);
    }
  };

  // Payment callback handling
  const paymentCallbackFunction = async () => {
    if (!orderId) return;

    const pg = sessionStorage.getItem("pg");
    setVerifyPaymentProgress(true);

    const navigateToSuccess = () => {
      setVerifyPaymentProgress(false);
      navigate(
        `/PaymentSuccessful/?&scl=${schoolCode}&order_id=${orderId}${
          itemIds ? `&item_ids=${itemIds}` : ""
        }`
      );
    };

    const verifyAndNavigate = async (retries: number, delay: number) => {
      for (let attempt = 1; attempt <= retries; attempt++) {
        const orderStatus =
          pg === "razorpay"
            ? await verifyPaymentRazorpay(orderId)
            : await verifyPayment(orderId);

        if (orderStatus) {
          navigateToSuccess();
          return;
        }

        if (attempt < retries) {
          await new Promise((resolve) => setTimeout(resolve, delay));
        }
      }
    };

    try {
      await verifyAndNavigate(3, 2000);

      const orderStatus =
        pg === "razorpay"
          ? await verifyPaymentRazorpay(orderId)
          : await verifyPayment(orderId);

      if (orderStatus) {
        navigateToSuccess();
      } else {
        setPaymentFailed(true);
        Sentry.captureMessage("Final verification failed - Other Issue");
      }
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setVerifyPaymentProgress(false);
    }
  };

  // Cart management functions
  const populateDefaultItems = (defaultItem: FinancialData) => {
    setDefaultItems([defaultItem]);
    // Store the base_price (MRP) in session storage for WebEngage events
    sessionStorage.setItem("mrp", defaultItem.base_price.toString());
  };

  const getAndSetAllItemsDB = async (
    schoolCode: string,
    mobileNumber: string,
    pfh: boolean
  ): Promise<ItemDetailsData[]> => {
    try {
      const tempAllItems = await fetchRecommendedItems(
        schoolCode,
        mobileNumber,
        pfh,
        referredBy
      );
      const allItems = tempAllItems.recommendedItems.filter(
        (item) => item.selling_price !== 0
      );
      populateDefaultItems(tempAllItems.default);
      setAllItemDetailsDB(allItems);
      return allItems;
    } catch (err) {
      Sentry.captureException(err);
      throw new Error(`Failed to fetch items: ${err}`);
    }
  };

  const populateFullCart = (
    allItems: ItemDetailsData[],
    itemsIdsAr: number[]
  ) => {
    const tempCheckItems = allItems.filter((item) =>
      itemsIdsAr.includes(item.id)
    );
    const tempRecoItems = allItems.filter(
      (item) => !itemsIdsAr.includes(item.id)
    );
    setCheckoutItems(tempCheckItems);
    setRecommendedItems(tempRecoItems);
  };

  const handleRemoveItem = (itemId: number) => {
    setCheckoutItemIds((prevItems) =>
      prevItems.filter((currId) => currId !== itemId)
    );
    setCheckoutItems((prevItems) =>
      prevItems.filter((item) => item.id !== itemId)
    );

    const removedItem = allItemDetailsDB.find((item) => item.id === itemId);
    if (removedItem) {
      setRecommendedItems((prevItems) => [...prevItems, removedItem]);
    }
  };

  const handleAddItemFromRecommended = (itemId: number) => {
    setCheckoutItemIds((prevIds) => [...prevIds, itemId]);
    setRecommendedItems((prevItems) =>
      prevItems.filter((item) => item.id !== itemId)
    );

    const addedItem = allItemDetailsDB.find((item) => item.id === itemId);
    if (addedItem) {
      setCheckoutItems((prevItems) => [...prevItems, addedItem]);
    }
  };

  // Initialize school and items
  useEffect(() => {
    if (schoolCode && !individualReg) {
      setLoading(true);
      getSchoolByCode(schoolCode)
        .then((schoolDetails) => {
          if (schoolDetails && schoolDetails.schoolID !== 0) {
            if (!schoolDetails.linkActive) {
              navigate(`/LinkUnavailable/?&scl=${schoolCode}`);
              return;
            }
            setBackendSchool(schoolDetails);
            sessionStorage.setItem(
              "school",
              `${schoolDetails.schoolName}, ${schoolDetails.city}`
            );
            sessionStorage.setItem("pg", schoolDetails.pg);
            sessionStorage.setItem(
              "isInternationalTransaction",
              JSON.stringify(schoolDetails.isInternational)
            );
            sessionStorage.setItem(
              "school",
              JSON.stringify(schoolDetails.schoolName)
            );
            setSchoolID(schoolDetails.schoolID);
            sessionStorage.setItem(
              "schoolID",
              JSON.stringify(schoolDetails.schoolID)
            );
            setIndividualReg(schoolDetails.individual_registration);
            sessionStorage.setItem(
              "individual_registration",
              JSON.stringify(schoolDetails.individual_registration)
            );
            sessionStorage.setItem("examDate", schoolDetails.examDate);

            if (schoolDetails.isInternational) {
              paymentCallbackFunctionInternational();
            } else {
              paymentCallbackFunction();
            }
          }
        })
        .then(() => {
          getAndSetAllItemsDB(schoolCode, userDetails.mobileNumber, false)
            .then((allItems) => {
              setCheckoutItemIds([]);
              populateFullCart(allItems, []);
              setLoading(false);
            })
            .catch((err) => {
              Sentry.captureException(err);
              setLoading(false);
            });
        })
        .catch((err) => {
          Sentry.captureException(err);
          setLoading(false);
        });
    }
  }, [schoolCode, navigate, individualReg]);

  useEffect(() => {
    if (schoolCode && pfhVerified) {
      console.log(
        "this is happening",
        schoolCode,
        userDetails.mobileNumber,
        pfhVerified
      );
      getAndSetAllItemsDB(schoolCode, userDetails.mobileNumber, pfhVerified)
        .then((allItems) => {
          setCheckoutItemIds([]);
          populateFullCart(allItems, []);
          setLoading(false);
        })
        .catch((err) => {
          Sentry.captureException(err);
          setLoading(false);
        });
    }
  }, [schoolCode, pfhVerified]);

  // Payment handling
  const handlePaymentClickNFO = async (data?: doNFOTransactionResponse) => {
    if (backendSchool.isInternational) {
      setLoading(true);
      navigate("/PaymentPage");
      return;
    }

    const sellingPrice = defaultItems[0]?.selling_price;
    sessionStorage.setItem(
      "transactionAmount",
      sellingPrice?.toString() || "0"
    );

    const itemIdsString = checkoutItems.map((item) => item.id).join("-");

    try {
      setLoading(true);

      if (!data?.data?.links?.[1]?.parameters?.bdorderid) {
        throw new Error("Invalid payment data");
      }

      const flow_config = {
        merchantId: data.data.links[1].parameters.mercid,
        bdOrderId: data.data.links[1].parameters.bdorderid,
        authToken: data.data.links[1].headers.authorization,
        childWindow: true,
        returnUrl: `https://nfoself.checkout.nationalfinanceolympiad.com/CheckoutPage/?&scl=${schoolCode}&order_id=${data.data.orderID}&item_ids=${itemIdsString}`,
        retryCount: 3,
        prefs: {
          payment_categories: ["card", "nb", "upi"],
        },
        netBanking: {
          showPopularBanks: "Y",
          popularBanks: ["Kotak Bank", " AXIS Bank [Retail]"],
        },
      };

      const responseHandler = (txn: { status: string; response: string }) => {
        Sentry.captureMessage(
          `Payment callback - Status: ${txn.status}, Response: ${txn.response}`,
          "info"
        );
      };

      const theme_config = {
        sdkPrimaryColor: "#023b8f",
        sdkAccentColor: "#023b8f",
        sdkBackgroundColor: "#FFF",
        sdkBannerColor: "#FFF",
      };

      const config = {
        responseHandler,
        merchantLogo: merchantLogoString,
        flowConfig: flow_config,
        flowType: "payments",
        themeConfig: theme_config,
      };

      if (window.loadBillDeskSdk) {
        window.loadBillDeskSdk(config);
      }
    } catch (err) {
      Sentry.captureException(err);
      throw new Error(`BillDesk Error: ${err}`);
    } finally {
      setLoading(false);
    }
  };

  const isPfh = urlParams.get("pfh");
  console.log("isPfh", isPfh);
  const decodedPfh = atob(isPfh || "");
  const [isPfhModalOpen, setIsPfhModalOpen] = useState(
    decodedPfh === "true" ? true : false
  );
  console.log("isPfhModalOpen", isPfhModalOpen, isPfh);
  console.log("userDetails", userDetails.mobileNumber);

  // In CheckoutPage.tsx, add a function to track form completion
  const trackFormCompletion = () => {
    const requiredFields = {
      name: userDetails.name,
      email: userDetails.email,
      mobileNumber: userDetails.mobileNumber,
      address: userDetails.address,
      flatNo: userDetails.flatNo,
      city: userDetails.city,
      state: userDetails.state,
      pincode: userDetails.pincode,
    };

    const isFormComplete = Object.values(requiredFields).every(
      (field) => field !== ""
    );

    if (isFormComplete) {
      ReactGA.event({
        category: "5678_checkout_fields_filled",
        action: "5678_checkout_fields_filled",
        label: "5678_checkout_fields_filled",
      });
    }
  };

  // Add useEffect to monitor form completion
  useEffect(() => {
    trackFormCompletion();
  }, [userDetails]);

  // Test function for WebEngage login and setAttribute
  const testWebEngageIntegration = () => {
    if (!window.webengage) {
      console.error("❌ WebEngage is not loaded");
      alert(
        "❌ WebEngage is not loaded. Please check if WebEngage script is included."
      );
      return;
    }

    // Get user details from form or sessionStorage
    const userName =
      userDetails.name || sessionStorage.getItem("name") || "Test User";
    const userEmail =
      userDetails.email ||
      sessionStorage.getItem("email") ||
      "test@example.com";
    const userPhone =
      userDetails.mobileNumber ||
      sessionStorage.getItem("mobileNumber") ||
      "1234567890";
    const userSchool =
      userDetails.school ||
      backendSchool.schoolName ||
      sessionStorage.getItem("school") ||
      "Test School";
    const userGrade =
      userDetails.grade || Number(sessionStorage.getItem("grade")) || 10;
    const examDate =
      backendSchool.examDate ||
      sessionStorage.getItem("examDate") ||
      new Date().toISOString();
    const studentUsername = `test_${Date.now()}`; // Generate test username

    try {
      // Step 1: WebEngage Login
      console.log("🔑 Testing WebEngage Login...");
      window.webengage.user.login(studentUsername);
      console.log(
        "✅ WebEngage Login successful with username:",
        studentUsername
      );

      // Step 2: Set User Attributes
      console.log("📝 Testing WebEngage setAttribute...");

      // Parse exam date and time
      const examScheduleTime = new Date(examDate);
      const examScheduleDate = new Date(examDate).toISOString().split("T")[0]; // YYYY-MM-DD format

      // Set user attributes
      window.webengage.user.setAttribute("phone", userPhone);
      window.webengage.user.setAttribute("email", userEmail);
      window.webengage.user.setAttribute("name", userName);
      window.webengage.user.setAttribute("grade", userGrade);
      window.webengage.user.setAttribute("school_name", userSchool);
      window.webengage.user.setAttribute(
        "exam_schedule_time",
        examScheduleTime
      );
      window.webengage.user.setAttribute(
        "exam_schedule_date",
        examScheduleDate
      );

      console.log("🎯 WebEngage user attributes set:", {
        phone: userPhone,
        email: userEmail,
        name: userName,
        grade: userGrade,
        school_name: userSchool,
        exam_schedule_time: examScheduleTime,
        exam_schedule_date: examScheduleDate,
      });

      console.log("🔍 Attribute format confirmation:", {
        "phone (type)": typeof userPhone,
        "email (type)": typeof userEmail,
        "name (type)": typeof userName,
        "grade (type)": typeof userGrade,
        "school_name (type)": typeof userSchool,
        "exam_schedule_time (type)": typeof examScheduleTime,
        "exam_schedule_time (constructor)": examScheduleTime.constructor.name,
        "exam_schedule_time (value)": examScheduleTime.toISOString(),
        "exam_schedule_date (type)": typeof examScheduleDate,
        "exam_schedule_date (constructor)": examScheduleDate.constructor.name,
        "exam_schedule_date (value)": examScheduleDate,
      });

      alert(
        "✅ WebEngage test completed successfully! Check console for details."
      );
    } catch (error) {
      console.error("❌ WebEngage test failed:", error);
      alert(`❌ WebEngage test failed: ${error}`);
    }
  };

  return (
    <div className="checkout">
      {verifyPaymentProgress || loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "#FFF",
          }}
        >
          <CircularProgress />
        </div>
      ) : (
        <UserContext.Provider
          value={{
            userDetails,
            setUserDetails,
            backendSchool,
            setBackendSchool,
            schoolID,
            setSchoolID,
            individualReg,
            setIndividualReg,
            examDate,
            setExamDate,
            isOtpVerified,
            setIsOtpVerified,
            pfhVerified,
            setPfhVerified,
          }}
        >
          <UniversalCheckoutHeader />

          {/* WebEngage Test Button - Only show in development */}
          {process.env.NODE_ENV === "development" && (
            <div
              style={{
                padding: "20px",
                textAlign: "center",
                backgroundColor: "#f0f8ff",
                border: "2px dashed #0066cc",
                margin: "20px",
              }}
            >
              <h3 style={{ color: "#0066cc", marginBottom: "10px" }}>
                🧪 WebEngage Test Panel
              </h3>
              <p
                style={{
                  fontSize: "14px",
                  color: "#666",
                  marginBottom: "15px",
                }}
              >
                Fill out the form below, then click this button to test
                WebEngage integration
              </p>
              <button
                onClick={testWebEngageIntegration}
                style={{
                  backgroundColor: "#0066cc",
                  color: "white",
                  border: "none",
                  padding: "12px 24px",
                  borderRadius: "6px",
                  fontSize: "16px",
                  fontWeight: "bold",
                  cursor: "pointer",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                }}
                onMouseOver={(e) =>
                  ((e.target as HTMLButtonElement).style.backgroundColor =
                    "#0052a3")
                }
                onMouseOut={(e) =>
                  ((e.target as HTMLButtonElement).style.backgroundColor =
                    "#0066cc")
                }
              >
                🚀 Test WebEngage Login & Attributes
              </button>
            </div>
          )}

          <CartSubtitle
            stateCheckoutItems={checkoutItems}
            stateDefaultItems={defaultItems}
            stateRecommendedItems={recommendedItems}
            cbRemoveProduct={handleRemoveItem}
            cbAddRecommended={handleAddItemFromRecommended}
            cbHandlePaymentClick={handlePaymentClickNFO}
          />
          <CheckoutPaymentFooter />
          <CheckoutFooter />
        </UserContext.Provider>
      )}

      <PaymentFailedModal
        paymentFailed={paymentFailed}
        setPaymentFailed={setPaymentFailed}
      />
      {isPfhModalOpen && (
        <PFHModal
          onVerificationFailed={(mobileNumber) => {
            setPfhVerified(false);
            setIsOtpVerified(false);
            setUserDetails((prev) => ({
              ...prev,
              mobileNumber,
              pfh: false,
            }));
          }}
          onClose={() => setIsPfhModalOpen(false)}
          isOpen={isPfhModalOpen}
          onVerificationSuccess={(mobileNumber, pfhVerified) => {
            if (!pfhVerified) {
              setShowAlert({
                isAlertVisible: true,
                alert:
                  "You've used your discount for the National Finance Olympiad 2025-26. Please proceed with standard pricing.",
              });
            }
            console.log("mobileNumber", mobileNumber);
            setPfhVerified(true);
            setIsOtpVerified(true);
            setUserDetails((prev) => ({
              ...prev,
              mobileNumber,
              pfh: true,
            }));
            setIsPfhModalOpen(false);
          }}
          isPFH={Boolean(isPfh)}
        />
      )}

      {showAlert.alert && (
        <Alert
          style={{
            borderRadius: 10,
            opacity: 1,
            position: "fixed",
            top: 5,
            left: 0,
            right: 0,
            backgroundColor: "#FFF",
            maxWidth: "380px",
            margin: "auto",
            maxHeight: "100px",
            alignItems: "center",
            animation:
              "springInFromTop 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55)",
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
            zIndex: 1000,
          }}
          severity="error"
        >
          {showAlert.alert}
        </Alert>
      )}
    </div>
  );
};

export default CheckoutPage;
